import {
  Table,
  Thead,
  Tr,
  Td,
  Th,
  Tbody,
  TableContainer,
} from '@chakra-ui/react';
import React, { useMemo } from 'react';

import {
  finleyAssert,
  typeguards as commonTypeguards,
  date as commonDate,
  utils as commonUtils,
} from '@finley-technologies/common';
import { Bank, Util } from '@finley-technologies/finley-types';

import {
  FormattedStatementColumnGQLType,
  FormattedStatementGQLType,
} from '../../../types/dashboard-core-gqlTypes.generated.d';
import { colors } from '~/packages/common-frontend/colors';
import { tableHeaderStyle } from '~/packages/common-frontend/styles';
import {
  formatCurrencyAmount,
  percentageToPercentageLabel,
} from '~/packages/common-frontend/utils';

// copied from zod v3.
type Primitive = string | number | symbol | bigint | boolean | null | undefined;

interface BaseColumn {
  type: 'currency' | 'percent' | 'date' | 'string';
  name: string;
  value: Primitive;
}

interface CurrencyColumn extends BaseColumn {
  type: 'currency';
  value: number | null;
}

interface InterestRateColumn extends BaseColumn {
  type: 'percent';
  value: number | null;
}

interface DateColumn extends BaseColumn {
  type: 'date';
  value: Util.FullDateStr | null;
}

interface StringColumn extends BaseColumn {
  type: 'string';
  value: string | null;
}

type MonthlyStatementDailyAggregationColumn =
  | CurrencyColumn
  | InterestRateColumn
  | DateColumn
  | StringColumn;

const formatMonthlyDailyStatementDailyAggregationColumn = ({
  columnDef,
  columnValue,
}: {
  columnDef: FormattedStatementColumnGQLType;
  columnValue: Primitive;
}): MonthlyStatementDailyAggregationColumn => {
  switch (columnDef.format.type) {
    case 'string': {
      return {
        type: 'string',
        name: columnDef.name,
        value: String(columnValue),
      };
    }
    case 'currency': {
      finleyAssert(
        typeof columnValue === 'number' || columnValue === null,
        `Invalid column value ${String(columnValue)} for currency column`,
      );
      return {
        type: 'currency',
        name: columnDef.name,
        value: columnValue,
      };
    }
    case 'percent': {
      finleyAssert(
        typeof columnValue === 'number' || columnValue === null,
        `Invalid column value ${String(columnValue)} for percent column`,
      );
      return {
        type: 'percent',
        name: columnDef.name,
        value: columnValue,
      };
    }
    case 'date': {
      finleyAssert(
        commonTypeguards.isFullDateStr(columnValue) || columnValue === null,
        `Invalid column value ${String(columnValue)} for date column`,
      );
      return {
        type: 'date',
        name: columnDef.name,
        value: columnValue,
      };
    }
    default:
      throw new Error(`Invalid column type [${columnDef.format.type}]`);
  }
};

const formatOpeningBalanceDataRow = ({
  row,
  columns,
}: {
  row: Primitive[];
  columns: FormattedStatementColumnGQLType[];
}): MonthlyStatementDailyAggregationColumn[] => {
  return row.map((col, index) => {
    if (index === 0 && col === 'Opening Balance') {
      return {
        type: 'string',
        name: 'Opening Balance',
        value: String(col),
      };
    }
    const columnDef = columns[index];
    return formatMonthlyDailyStatementDailyAggregationColumn({
      columnDef,
      columnValue: col,
    });
  });
};

const attachTypeToData = ({
  columns,
  rows,
}: {
  columns: FormattedStatementColumnGQLType[];
  rows: Primitive[][];
}): MonthlyStatementDailyAggregationColumn[][] =>
  rows.map((row) =>
    row.map((col, index) => {
      const columnDef = columns[index];
      return formatMonthlyDailyStatementDailyAggregationColumn({
        columnDef,
        columnValue: col,
      });
    }),
  );

const formatColumnValue = (
  column: MonthlyStatementDailyAggregationColumn,
): string => {
  if (column.value === null) {
    return '-';
  }
  switch (column.type) {
    case 'string':
      return column.value ?? '-';
    case 'currency':
      // @jendevelops MSR-TODO: add currency type
      return formatCurrencyAmount(column.value, Bank.Currency.USD) || '-';
    case 'percent':
      return percentageToPercentageLabel(column.value * 100, 2) || '-';
    case 'date':
      return commonDate.fullDateStrToHumanDateString(column.value);
    default:
      throw new Error('Invalid column type');
  }
};

export const MonthlyStatementDailyAggregateTable = ({
  dailyAggregateData,
}: {
  dailyAggregateData: FormattedStatementGQLType;
}) => {
  const tableData = useMemo(() => {
    const firstRow = [
      formatOpeningBalanceDataRow({
        row: dailyAggregateData.rows[0],
        columns: dailyAggregateData.columns,
      }),
    ];

    const otherRows = attachTypeToData({
      columns: dailyAggregateData.columns,
      rows: dailyAggregateData.rows.slice(1),
    });

    return firstRow.concat(otherRows);
  }, [dailyAggregateData.columns, dailyAggregateData.rows]);

  return (
    <TableContainer>
      <Table
        sx={{
          borderColor: colors.gray[100],
          borderWidth: '1px',
          bg: 'white',
          textColor: colors.gray[700],
        }}
      >
        <Thead>
          <Tr>
            {dailyAggregateData.columns.map(({ name }) => (
              <Th key={name} sx={tableHeaderStyle}>
                {commonUtils.toTitleCase(name)}
              </Th>
            ))}
          </Tr>
        </Thead>
        <Tbody>
          {tableData.map((columns) => {
            return (
              <Tr key={columns[0].value}>
                {columns.map((col) => (
                  <Td
                    key={col.name}
                    sx={{
                      backgroundColor: 'white',
                      px: '1rem',
                      color: colors.gray[600],
                      whiteSpace: 'normal',
                    }}
                  >
                    {formatColumnValue(col)}
                  </Td>
                ))}
              </Tr>
            );
          })}
        </Tbody>
      </Table>
    </TableContainer>
  );
};
